import { cn } from "@/lib/utils";
import { money } from "@/lib/api";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold" data-testid="page-title">
          {title}
        </h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, hint, tone = "primary", testid }) {
  const tones = {
    primary: "text-primary bg-accent",
    success: "text-[hsl(var(--success))] bg-[hsl(var(--success)/0.12)]",
    warning: "text-[hsl(var(--warning))] bg-[hsl(var(--warning)/0.12)]",
    muted: "text-muted-foreground bg-muted",
  };
  return (
    <div
      className="rounded-xl border border-border bg-card card-shadow p-6 transition-transform duration-200 hover:-translate-y-1"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] font-medium text-muted-foreground">
            {label}
          </p>
          <p className="text-3xl font-semibold mt-3 font-display tracking-tight">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-2">{hint}</p>}
        </div>
        {Icon && (
          <div className={cn("w-11 h-11 rounded-lg grid place-items-center", tones[tone])}>
            <Icon className="w-5 h-5" weight="duotone" />
          </div>
        )}
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, testid }) {
  return (
    <div
      className="rounded-xl border-2 border-dashed border-border bg-card/40 p-12 flex flex-col items-center text-center"
      data-testid={testid}
    >
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-accent text-primary grid place-items-center mb-5">
          <Icon className="w-7 h-7" weight="duotone" />
        </div>
      )}
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground mt-2 max-w-sm">{description}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function RoleBadge({ role }) {
  const map = {
    admin: "bg-primary/10 text-primary border-primary/20",
    reseller: "bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.25)]",
    customer: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.25)]",
    platform: "bg-muted text-muted-foreground border-border",
  };
  return (
    <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize", map[role] || map.customer)}>
      {role}
    </span>
  );
}

export function StatusPill({ status }) {
  const ok = ["active"].includes(status);
  const bad = ["suspended", "stopped"].includes(status);
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border",
      ok && "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.25)]",
      bad && "bg-destructive/10 text-destructive border-destructive/20",
      !ok && !bad && "bg-muted text-muted-foreground border-border"
    )}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      <span className="capitalize">{status}</span>
    </span>
  );
}

export { money };
